import React from "react";

import { useGetPracticeCrops, useTranslation } from "utils";

import { CMC_PH_TYPE, CMC_SOIL_TYPE, CROP_ROTATION, CropItemData, TimelineCalendarItemsProps } from "models";

import { Divider, Flex, Icon, Radio, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CMC_ONBOARDING_1_ERRORS } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { AddEditCropsSection } from "pages/app/crop-mix-composer/components/AddEditCropsSection.tsx";
import { CropMixComposerMultiSelect } from "pages/app/crop-mix-composer/components/CropMixComposerMultiSelect.tsx";

const TitleSection = ({ emoji, title }: { emoji: string; title: string }) => (
  <Flex gap="1rem">
    <Typography variant="paragraph-l" data-testid="cmc-crop-and-soil-conditions-crop-selection-icon" className="margin-0">
      {emoji}
    </Typography>
    <Typography variant="paragraph-l" data-testid="cmc-crop-and-soil-conditions-crop-selection-title" className="margin-0">
      {title}
    </Typography>
  </Flex>
);

export const CROP_FAMILIES_TO_EXCLUDE_OPTIONS = [CROP_ROTATION.FABACEAE, CROP_ROTATION.BRASSICACEAE, CROP_ROTATION.SOLANACEAE, CROP_ROTATION.AMARANTHACEAE];

export const SequenceAndConditions = ({
  selectedRecommendationItemOnTimeline,
  fieldId,
}: {
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>;
  fieldId?: string;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const {
    soilPh,
    setSoilPh,
    soilType,
    setSoilType,
    drought,
    setDrought,
    cropRotationFamiliesToExclude,
    setCropRotationFamiliesToExclude,
    legumeExclusionConfirmation,
    setLegumeExclusionConfirmation,
    mainCropSettings,
    isChangingStep,
    onboarding1Errors,
  } = useCropMixComposerContext();
  const { data: crops } = useGetPracticeCrops({ enabled: true });

  const handleDrought = (value: string) => {
    if (isChangingStep !== null) return;

    if (value === "yes") setDrought(true);
    if (value === "no") setDrought(false);
  };

  const getDrought = () => {
    if (drought) return ["yes"];
    if (drought === false) return ["no"];
    return [];
  };

  const crop1Family = mainCropSettings?.crop1Id ? crops?.find((crop) => crop.id === mainCropSettings?.crop1Id)?.cropFamily : null;
  const crop2Family = mainCropSettings?.crop2Id ? crops?.find((crop) => crop.id === mainCropSettings?.crop2Id)?.cropFamily : null;
  const shouldShowLegumeConfirmation =
    cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) || crop1Family === CROP_ROTATION.FABACEAE || crop2Family === CROP_ROTATION.FABACEAE;
  const disabledCropFamilyOptions = [...(crop1Family ? [crop1Family] : []), ...(crop2Family ? [crop2Family] : [])].filter((family) =>
    CROP_FAMILIES_TO_EXCLUDE_OPTIONS.includes(family)
  );

  return (
    <Flex className="margin-top-200 margin-bottom-200" vertical={true}>
      <Flex vertical={true} gap={"2rem"}>
        <TitleSection emoji="🌽" title={t("cropAndSoilConditions.cropSelection.title")} />
        <AddEditCropsSection fieldId={fieldId} selectedRecommendationItemOnTimeline={selectedRecommendationItemOnTimeline} />
      </Flex>
      <Divider dashed={true} />
      <Flex vertical={true} gap={"2rem"}>
        <TitleSection emoji="🌿" title={t("cropAndSoilConditions.cropRotationFamiliesToExclude.title")} />
        <Flex vertical={true} gap={"1rem"}>
          <CropMixComposerMultiSelect
            items={CROP_FAMILIES_TO_EXCLUDE_OPTIONS.map((item) => ({ name: t(`plantFamilies.${item}`), value: item }))}
            selectedItems={cropRotationFamiliesToExclude}
            onSelectionChanged={(value) => {
              if (isChangingStep !== null) return;

              if (value === CROP_ROTATION.FABACEAE && cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE)) {
                setLegumeExclusionConfirmation(null);
              }

              setCropRotationFamiliesToExclude((prev) => {
                if (prev.includes(value as CROP_ROTATION)) {
                  return prev.filter((item) => item !== value);
                }
                return [...prev, value as CROP_ROTATION];
              });
            }}
            disabledOptions={disabledCropFamilyOptions}
            dataTestId={"sequence-conditions-crop-rotation-families-to-exclude"}
          />
          <Flex gap={"0.625rem"} align="center">
            <Icon iconName="tabler/alert-circle" size="s" />
            <Typography variant="paragraph-m" className="margin-0">
              {" "}
              {t("cropAndSoilConditions.cropRotationFamiliesToExclude.info")}
            </Typography>
          </Flex>
          {shouldShowLegumeConfirmation && (
            <Flex vertical={true} gap={"1rem"} className="legume-confirmation-container">
              <Typography variant="h6" className="margin-0">
                {" "}
                {t("cropAndSoilConditions.cropRotationFamiliesToExclude.legumeConfirmation.title")}
              </Typography>
              <Typography variant="paragraph-m" className="margin-0">
                {" "}
                {t("cropAndSoilConditions.cropRotationFamiliesToExclude.legumeConfirmation.body")}
              </Typography>
              <div>
                <Radio.Group
                  defaultValue={legumeExclusionConfirmation}
                  onChange={(v) => {
                    if (isChangingStep !== null) return;

                    setLegumeExclusionConfirmation(v.target.value);
                  }}
                >
                  <Flex gap={"1rem"}>
                    <Radio data-testid="crop-family-exclude-legume-yes" value={true}>
                      {t("cropAndSoilConditions.cropRotationFamiliesToExclude.legumeConfirmation.yes")}
                    </Radio>
                    <Radio data-testid="crop-family-exclude-legume-no" value={false}>
                      {t("cropAndSoilConditions.cropRotationFamiliesToExclude.legumeConfirmation.no")}
                    </Radio>
                  </Flex>
                </Radio.Group>
                {onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.LEGUME_EXCLUSION_CONFIRMATION) && (
                  <Typography variant="paragraph-m" className="cmc-onboarding-step-error-message">
                    {t("errors.selectOneAnswer")}
                  </Typography>
                )}
              </div>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Divider dashed={true} />
      <Flex vertical={true} gap={"2rem"}>
        <TitleSection emoji="🌱" title={t("cropAndSoilConditions.soilType.title")} />
        <div>
          <Typography variant="paragraph-s" className="margin-top-0">
            {" "}
            {t("cropAndSoilConditions.soilType.soilType")}
          </Typography>
          <CropMixComposerMultiSelect
            items={Object.values(CMC_SOIL_TYPE).map((item) => ({ name: t(`cropAndSoilConditions.soilType.${item}`), value: item }))}
            selectedItems={soilType ? [soilType] : []}
            onSelectionChanged={(value) => {
              if (isChangingStep !== null) return;

              setSoilType(value as CMC_SOIL_TYPE);
            }}
            dataTestId={"sequence-conditions-soil-type"}
          />
          {onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.SOIL_TYPE) && (
            <Typography variant="paragraph-m" className="cmc-onboarding-step-error-message">
              {t("errors.addMissingData")}
            </Typography>
          )}
        </div>
        <div>
          <Typography variant="paragraph-s" className="margin-top-0">
            {" "}
            {t("cropAndSoilConditions.soilType.soilPh")}
          </Typography>
          <CropMixComposerMultiSelect
            items={Object.values(CMC_PH_TYPE).map((item) => ({ name: t(`cropAndSoilConditions.soilType.${item}`), value: item }))}
            selectedItems={soilPh ? [soilPh] : []}
            onSelectionChanged={(value) => {
              if (isChangingStep !== null) return;

              setSoilPh(value as CMC_PH_TYPE);
            }}
            dataTestId={"sequence-conditions-soil-ph"}
          />
          {onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.SOIL_PH) && (
            <Typography variant="paragraph-m" className="cmc-onboarding-step-error-message">
              {t("errors.addMissingData")}
            </Typography>
          )}
        </div>
      </Flex>
      <Divider dashed={true} />
      <Flex vertical={true} gap={"2rem"}>
        <TitleSection emoji="️☀️" title={t("cropAndSoilConditions.droughtProne.title")} />
        <div>
          <CropMixComposerMultiSelect
            items={[
              { name: t("cropAndSoilConditions.droughtProne.options.yes"), value: "yes" },
              { name: t("cropAndSoilConditions.droughtProne.options.no"), value: "no" },
            ]}
            selectedItems={getDrought()}
            onSelectionChanged={handleDrought}
            dataTestId={"sequence-conditions-drought"}
          />
          {onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.DROUGHT) && (
            <Typography variant="paragraph-m" className="cmc-onboarding-step-error-message">
              {t("errors.addMissingData")}
            </Typography>
          )}
        </div>
      </Flex>
    </Flex>
  );
};
